import { useMemo, useState } from "react";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { api } from "../lib/api";
import { useResource, formatDate, formatDateTime, daysUntil } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { FilterBar, matches } from "../components/FilterBar";
import { EntityForm, Field } from "../components/EntityForm";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Modal } from "../components/Modal";
import { uploadFile, fileViewUrl, fileDownloadUrl } from "../lib/files";
import type { PartnerDocument, Policy } from "../lib/types";

interface PolicyFull extends Policy {
    body_url: string | null;
    file_id: string | null;
    superseded_by_id: string | null;
    revoked_at: string | null;
    revoked_reason: string | null;
}

interface MyAcknowledgement {
    policy_id: string;
    acknowledged_at: string;
}

interface AckRow {
    full_name: string;
    role: string;
    acknowledged_at: string;
}

const CATEGORIES = ["Clinical", "Governance", "Compliance", "WHS", "Privacy", "Human resources"]
    .map((c) => ({ value: c, label: c }));

const STATUS = [
    { value: "current", label: "Current" },
    { value: "under_review", label: "Under review" },
    { value: "archived", label: "Archived" },
];

/**
 * A policy's real state is not only its status column. Revoked and superseded
 * both land on `archived`; the distinguishing evidence is the reason and the
 * successor, so the label derives from those.
 */
const stateOf = (policy: PolicyFull): { label: string; chip: string } => {
    if (policy.revoked_at) return { label: "Revoked", chip: "block" };
    if (policy.superseded_by_id) return { label: "Superseded", chip: "mute" };
    if (policy.status === "under_review") return { label: "Under review", chip: "warn" };
    if (policy.status === "archived") return { label: "Archived", chip: "mute" };
    return { label: "Current", chip: "pass" };
};

type Dialog =
    | { kind: "create" }
    | { kind: "edit"; policy: PolicyFull }
    | { kind: "revoke"; policy: PolicyFull }
    | { kind: "supersede"; policy: PolicyFull }
    | { kind: "upload"; policy: PolicyFull }
    | { kind: "acknowledge"; policy: PolicyFull }
    | { kind: "acks"; policy: PolicyFull }
    | null;

const AcknowledgementList = ({ policy, onClose }: { policy: PolicyFull; onClose: () => void }) => {
    const acks = useResource<AckRow[]>(`/policies/${policy.id}/acknowledgements`);
    return (
        <Modal title={`Acknowledgements — ${policy.name}`} onClose={onClose}>
            {acks.loading ? (
                <p style={{ color: "var(--muted)" }}>Loading…</p>
            ) : (acks.data ?? []).length === 0 ? (
                <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>Nobody has acknowledged this policy yet.</p>
            ) : (
                <div className="table-wrap">
                    <table>
                        <thead><tr><th>Who</th><th>Role</th><th>When</th></tr></thead>
                        <tbody>
                            {(acks.data ?? []).map((row, i) => (
                                <tr key={i}>
                                    <td>
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", fontWeight: 600 }}>
                                            <InitialsAvatar name={row.full_name} size={24} />
                                            {row.full_name}
                                        </div>
                                    </td>
                                    <td><span className="chip info">{row.role}</span></td>
                                    <td className="mono">{formatDateTime(row.acknowledged_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Modal>
    );
};

/**
 * A provider's own policy library. They upload; both sides can read — the
 * partner here, TrustCare staff on the provider profile's Documents tab.
 * Company stamping and visibility are RLS's job, not this component's.
 */
const OrgPoliciesCard = () => {
    const docs = useResource<PartnerDocument[]>("/partner-documents");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const mine = (docs.data ?? []).filter((d) => d.kind === "policy" && !d.participant_id);

    const upload = async (file: File) => {
        setBusy(true);
        setError(null);
        try {
            // Partners store bytes as 'general' — the partner_document row carries the meaning.
            const stored = await uploadFile(file, "general");
            await api.post("/partner-documents", { fileId: stored.id, kind: "policy" });
            docs.reload();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="card" style={{ marginBottom: "1rem" }}>
            <div className="card-head">
                <div>
                    <h2>Your organisation&apos;s policies</h2>
                    <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "0.2rem" }}>
                        Upload your own policies and procedures here. The TrustCare team can see
                        them too — one shared copy instead of email attachments.
                    </p>
                </div>
                <label className={`btn small${busy ? " disabled" : ""}`} style={{ cursor: busy ? "wait" : "pointer" }}>
                    {busy ? "Uploading…" : "+ Upload policy"}
                    <input type="file" accept=".pdf,.doc,.docx,.txt" style={{ display: "none" }}
                        disabled={busy}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void upload(file);
                            e.target.value = "";
                        }} />
                </label>
            </div>
            <div className="card-body">
                {error && <div className="error-banner">{error}</div>}
                {mine.length === 0 ? (
                    <p className="empty">Nothing uploaded yet. Your policies appear here for both your team and TrustCare.</p>
                ) : (
                    <ul className="managed-list-items">
                        {mine.map((doc) => (
                            <li key={doc.id}>
                                <span>
                                    {doc.file_name}
                                    <span className="muted"> · {doc.uploaded_by_name ?? "—"} · {formatDate(doc.created_at)}</span>
                                </span>
                                <span className="managed-list-actions">
                                    <a className="icon-btn" href={fileViewUrl(doc.file_id)} target="_blank" rel="noreferrer">View</a>
                                    <a className="icon-btn" href={fileDownloadUrl(doc.file_id)}>Download</a>
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export const Policies = () => {
    const { user } = useAuth();
    const canManage = user?.role === "admin" || user?.role === "manager";

    const policies = useResource<PolicyFull[]>("/policies");
    const myAcks = useResource<MyAcknowledgement[]>("/policies/acknowledgements/mine");

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [dialog, setDialog] = useState<Dialog>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const refresh = () => { policies.reload(); myAcks.reload(); setDialog(null); };

    const acknowledged = useMemo(
        () => new Map((myAcks.data ?? []).map((a) => [a.policy_id, a.acknowledged_at])),
        [myAcks.data]
    );

    const byId = useMemo(
        () => new Map((policies.data ?? []).map((p) => [p.id, p])),
        [policies.data]
    );

    const fields: Field[] = [
        { name: "name", label: "Policy name", kind: "text", required: true },
        { name: "category", label: "Category", kind: "select", required: true, options: CATEGORIES, half: true },
        { name: "version", label: "Version", kind: "text", required: true, half: true, placeholder: "e.g. 2.1" },
        { name: "status", label: "Status", kind: "select", options: STATUS, half: true },
        { name: "effective_date", label: "Effective from", kind: "date", required: true, half: true },
        { name: "review_date", label: "Next review", kind: "date", half: true },
        { name: "audience", label: "Audience", kind: "select", half: true, options: [
            { value: "internal", label: "Internal — staff only" },
            { value: "partner", label: "Partners only" },
            { value: "all", label: "Everyone (staff and partners)" },
        ], help: "Partner logins only ever see policies published to partners or everyone." },
    ];

    const attach = async (policy: PolicyFull, file: File) => {
        setUploading(true);
        setUploadError(null);
        try {
            const stored = await uploadFile(file, "policy");
            await api.put(`/policies/${policy.id}`, { file_id: stored.id });
            refresh();
        } catch (err) {
            setUploadError(err instanceof Error ? err.message : "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const all = policies.data ?? [];
    const filtered = all.filter((p) =>
        matches(search, p.name, p.category) &&
        (!filters.category || p.category === filters.category) &&
        (!filters.status || stateOf(p).label.toLowerCase().replace(" ", "_") === filters.status)
    );

    const counts = CATEGORIES.map((c) => ({
        name: c.label,
        count: all.filter((p) => p.category === c.label).length,
    })).filter((c) => c.count > 0);

    const unacknowledgedCount = all.filter(
        (p) => stateOf(p).label === "Current" && !acknowledged.has(p.id)
    ).length;

    if (policies.error) return <div className="error-banner">{policies.error}</div>;

    return (
        <div>
            {dialog?.kind === "create" && (
                <EntityForm title="Add Policy" path="/policies" fields={fields}
                    subtitle="Save the details and you'll be asked for the document straight away."
                    onClose={() => setDialog(null)}
                    onSaved={(saved) => {
                        refresh();
                        // Flow straight into attaching the document — one
                        // motion, not two separate chores.
                        const created = saved as PolicyFull | undefined;
                        if (created?.id) setDialog({ kind: "upload", policy: created });
                    }} />
            )}
            {dialog?.kind === "edit" && (
                <EntityForm title={`Edit ${dialog.policy.name}`} path="/policies"
                    fields={fields} initial={dialog.policy}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "upload" && (
                <Modal title={`Upload document — ${dialog.policy.name}`}
                    subtitle="Replaces the current file. The old version stays in storage."
                    onClose={() => setDialog(null)}>
                    {uploadError && <div className="error-banner">{uploadError}</div>}
                    <div className="field">
                        <label htmlFor="pol-file">Policy document (PDF preferred, max 10 MB)</label>
                        <input id="pol-file" type="file" accept=".pdf,.doc,.docx,.txt"
                            disabled={uploading}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) void attach(dialog.policy, file);
                            }} />
                        {uploading && <span className="help">Uploading…</span>}
                    </div>
                </Modal>
            )}
            {dialog?.kind === "acknowledge" && (
                <ConfirmDialog
                    title={`Acknowledge ${dialog.policy.name} v${dialog.policy.version}?`}
                    message="You are confirming you have read and understood this policy."
                    warning="Your name and the time are recorded permanently. An acknowledgement cannot be withdrawn."
                    confirmLabel="I have read and understood it"
                    request={{ method: "post", path: `/policies/${dialog.policy.id}/acknowledge` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "acks" && (
                <AcknowledgementList policy={dialog.policy} onClose={() => setDialog(null)} />
            )}
            {dialog?.kind === "revoke" && (
                <ConfirmDialog
                    title={`Revoke ${dialog.policy.name} v${dialog.policy.version}?`}
                    tone="danger"
                    message="The policy is withdrawn and no longer current. It is not replaced by anything."
                    warning="The document stays in the library, marked revoked, with your reason attached. Policies are never deleted — an auditor must still be able to read what was in force at any past date."
                    confirmLabel="Revoke policy" requireReason reasonLabel="Reason for revoking"
                    request={{ method: "post", path: `/policies/${dialog.policy.id}/revoke` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}
            {dialog?.kind === "supersede" && (
                <EntityForm
                    title={`Supersede ${dialog.policy.name} v${dialog.policy.version}`}
                    subtitle="Point this policy at the version that replaces it. The chain stays walkable at audit."
                    path={`/policies/${dialog.policy.id}/supersede`}
                    fields={[{
                        name: "supersededById", label: "Replaced by", kind: "select", required: true,
                        options: all
                            .filter((p) => p.id !== dialog.policy.id && !p.revoked_at)
                            .map((p) => ({ value: p.id, label: `${p.name} v${p.version}` })),
                    }]}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}

            {user?.role === "partner" && <OrgPoliciesCard />}

            {unacknowledgedCount > 0 && (
                <div className="alert-banner">
                    <h3>You have {unacknowledgedCount} polic{unacknowledgedCount === 1 ? "y" : "ies"} to acknowledge</h3>
                    <p style={{ fontSize: "0.82rem" }}>
                        Read each one, then use its <strong>Acknowledge</strong> action. Your acknowledgement is
                        recorded with your name and the time.
                    </p>
                </div>
            )}

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search policies…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[
                    { name: "category", allLabel: "All Categories", options: CATEGORIES },
                    { name: "status", allLabel: "All Status", options: [
                        { value: "current", label: "Current" },
                        { value: "under_review", label: "Under review" },
                        { value: "archived", label: "Archived" },
                        { value: "superseded", label: "Superseded" },
                        { value: "revoked", label: "Revoked" },
                    ] },
                ]}
                resultNote={filtered.length !== all.length ? `Showing ${filtered.length} of ${all.length} policies` : undefined}
                action={canManage && (
                    <button className="btn" onClick={() => setDialog({ kind: "create" })}>+ Add Policy</button>
                )}
            />

            {counts.length > 0 && (
                <div className="grid cols-4" style={{ marginBottom: "1rem" }}>
                    {counts.map((c) => (
                        <div key={c.name} className="card">
                            <div className="stat">
                                <div className="label">{c.name}</div>
                                <div className="value">{c.count}</div>
                                <div className="sub">{c.count === 1 ? "policy" : "policies"}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="card">
                <div className="card-head"><h2>All policies</h2></div>
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Policy name</th><th>Category</th><th>Version</th>
                                    <th>Review</th><th>Status</th><th>My acknowledgement</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((policy) => {
                                    const state = stateOf(policy);
                                    const overdue = policy.review_date && daysUntil(policy.review_date) < 0 && !policy.revoked_at;
                                    const successor = policy.superseded_by_id ? byId.get(policy.superseded_by_id) : null;
                                    const withdrawn = Boolean(policy.revoked_at || policy.superseded_by_id);
                                    const myAck = acknowledged.get(policy.id);

                                    return (
                                        <tr key={policy.id}>
                                            <td>
                                                <div style={{ fontWeight: 600 }}>{policy.name}</div>
                                                {policy.revoked_reason && (
                                                    <div style={{ fontSize: "0.73rem", color: "var(--block)" }}>
                                                        Revoked: {policy.revoked_reason}
                                                    </div>
                                                )}
                                                {successor && (
                                                    <div style={{ fontSize: "0.73rem", color: "var(--muted)" }}>
                                                        Replaced by {successor.name} v{successor.version}
                                                    </div>
                                                )}
                                            </td>
                                            <td><span className="chip info">{policy.category}</span></td>
                                            <td className="mono">v{policy.version}</td>
                                            <td className="mono">
                                                {formatDate(policy.review_date)}
                                                {overdue && <span className="chip warn" style={{ marginLeft: 5 }}>overdue</span>}
                                            </td>
                                            <td><span className={`chip ${state.chip}`}>{state.label}</span></td>
                                            <td>
                                                {myAck ? (
                                                    <span className="chip pass" title={formatDateTime(myAck)}>acknowledged</span>
                                                ) : state.label === "Current" ? (
                                                    <button className="icon-btn" onClick={() => setDialog({ kind: "acknowledge", policy })}>
                                                        Acknowledge
                                                    </button>
                                                ) : (
                                                    <span className="chip mute">—</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="row-actions">
                                                    {policy.file_id ? (
                                                        <>
                                                            <a className="icon-btn" title="View" aria-label={`View ${policy.name}`}
                                                               href={fileViewUrl(policy.file_id)} target="_blank" rel="noreferrer">
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
                                                                </svg>
                                                            </a>
                                                            <a className="icon-btn" title="Download" aria-label={`Download ${policy.name}`}
                                                               href={fileDownloadUrl(policy.file_id)}>
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                    <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" />
                                                                </svg>
                                                            </a>
                                                        </>
                                                    ) : (
                                                        <span className="chip mute" title="No document uploaded yet">no file</span>
                                                    )}
                                                    {canManage && (
                                                        <>
                                                            <button className="icon-btn" onClick={() => { setUploadError(null); setDialog({ kind: "upload", policy }); }}>
                                                                Upload
                                                            </button>
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "acks", policy })}>
                                                                Acks
                                                            </button>
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "edit", policy })}>Edit</button>
                                                            {!withdrawn && (
                                                                <>
                                                                    <button className="icon-btn" onClick={() => setDialog({ kind: "supersede", policy })}>Supersede</button>
                                                                    <button className="icon-btn danger" onClick={() => setDialog({ kind: "revoke", policy })}>Revoke</button>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={7} className="empty">No policies match.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
